import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaHistory, FaChevronLeft, FaChevronRight, FaSpinner,
  FaChevronDown, FaChevronUp, FaSearch, FaTimes,
} from 'react-icons/fa';
import { fetchActivityLogs } from '../services/activityLogService';
import { isDemoMode } from '../utils/demoMode';

// ─── Constants ────────────────────────────────────────────────────────────────

const RESOURCE_OPTIONS = [
  { value: '', label: 'All Modules' },
  { value: 'job', label: 'Jobs' },
  { value: 'client', label: 'Clients' },
  { value: 'franchisee', label: 'Franchisees' },
  { value: 'household', label: 'Households' },
  { value: 'invoice', label: 'Invoices' },
  { value: 'rcti', label: 'RCTIs' },
  { value: 'franchiseeInvoice', label: 'FSE Invoices' },
  { value: 'jobSchedule', label: 'Job Schedules' },
  { value: 'serviceRequest', label: 'Service Requests' },
  { value: 'serviceType', label: 'Service Types' },
  { value: 'xero', label: 'Xero' },
  { value: 'upload', label: 'Uploads' },
  { value: 'allowedEmail', label: 'Allowed Emails' },
  { value: 'utility', label: 'Utilities' },
  { value: 'auth', label: 'Auth' },
  { value: 'signup', label: 'Signups' },
  { value: 'config', label: 'Config' },
  { value: 'report', label: 'Reports' },
  { value: 'data', label: 'Data' },
  { value: 'scheduler', label: 'Scheduler' },
];

const METHOD_STYLES = {
  POST:   'bg-blue-100 text-blue-700',
  PUT:    'bg-yellow-100 text-yellow-700',
  PATCH:  'bg-orange-100 text-orange-700',
  DELETE: 'bg-red-100 text-red-700',
};

const RESOURCE_LINKS = {
  job: (id) => `/jobs-list`,                  // link to job module (detail links differ per app)
  client: (id) => `/clients`,
  franchisee: (id) => `/franchisees`,
  household: (id) => `/households`,
  invoice: (id) => `/invoices`,
};

const PAGE_SIZE = 50;

// Path suffixes considered "reads" — hidden by default
const READ_SUFFIXES = [
  '/list', '/details', '/audit-log', '/preview',
  '/search', '/validate', '/compare', '/status', '/report',
];

function isReadEntry(entry) {
  if (!entry.path) return false;
  const p = entry.path.split('?')[0];
  return READ_SUFFIXES.some(s => p.endsWith(s));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function MethodBadge({ method }) {
  const cls = METHOD_STYLES[method] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
      {method}
    </span>
  );
}

// ─── Row (expandable) ─────────────────────────────────────────────────────────

function LogRow({ entry }) {
  const [expanded, setExpanded] = useState(false);

  const resourceLink = RESOURCE_LINKS[entry.resource];

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(v => !v)}
      >
        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
          {formatDate(entry.timestamp)}
        </td>
        <td className="px-4 py-3 text-xs text-gray-700 max-w-[160px] truncate">
          {entry.performedBy?.email || entry.performedBy?.uid || '—'}
        </td>
        <td className="px-4 py-3">
          <MethodBadge method={entry.method} />
        </td>
        <td className="px-4 py-3 text-xs">
          <span className="inline-block bg-gray-100 text-gray-700 rounded px-2 py-0.5 font-medium">
            {entry.resource || '—'}
          </span>
        </td>
        <td className="px-4 py-3 text-xs text-gray-600">
          {entry.resourceId ? (
            resourceLink ? (
              <Link
                to={resourceLink(entry.resourceId)}
                className="text-jims-cyan hover:underline"
                onClick={e => e.stopPropagation()}
              >
                {entry.resourceId}
              </Link>
            ) : (
              <span>{entry.resourceId}</span>
            )
          ) : '—'}
        </td>
        <td className="px-4 py-3 text-xs text-gray-400 max-w-[220px] truncate font-mono">
          {entry.path}
        </td>
        <td className="px-4 py-3 text-xs text-gray-400 text-center">
          {entry.statusCode}
        </td>
        <td className="px-4 py-3 text-center text-gray-400">
          {expanded ? <FaChevronUp className="inline h-3 w-3" /> : <FaChevronDown className="inline h-3 w-3" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={8} className="px-6 py-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">Request Body</p>
            {entry.body && Object.keys(entry.body).length > 0 ? (
              <pre className="text-xs bg-white border border-gray-200 rounded p-3 overflow-auto max-h-64 text-gray-700">
                {JSON.stringify(entry.body, null, 2)}
              </pre>
            ) : (
              <p className="text-xs text-gray-400 italic">No body data recorded.</p>
            )}
            <p className="text-xs text-gray-400 mt-2 font-mono">Full path: {entry.path}</p>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ActivityLogPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [resource, setResource] = useState('');
  const [email, setEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showReads, setShowReads] = useState(false);

  // Pagination
  const [cursorStack, setCursorStack] = useState([null]); // null = first page
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);

  const load = useCallback(async (cursor) => {
    setLoading(true);
    setError(null);
    try {
      const filters = { limit: PAGE_SIZE, cursor };
      if (resource) filters.resource = resource;
      if (email) filters.email = email;
      if (from) filters.from = new Date(from).toISOString();
      if (to) {
        // Include the whole "to" day
        const toEnd = new Date(to);
        toEnd.setHours(23, 59, 59, 999);
        filters.to = toEnd.toISOString();
      }
      const res = await fetchActivityLogs(filters);
      setEntries(res.data.entries || []);
      setNextCursor(res.data.nextCursor || null);
    } catch (err) {
      setError(err.message || 'Failed to load activity logs.');
    } finally {
      setLoading(false);
    }
  }, [resource, email, from, to]);

  // Reload from page 1 whenever filters change
  useEffect(() => {
    setCursorStack([null]);
    setPageIndex(0);
  }, [resource, email, from, to]);

  useEffect(() => {
    load(cursorStack[pageIndex]);
  }, [pageIndex, cursorStack, load]);

  const handleNext = () => {
    if (!nextCursor) return;
    const newStack = cursorStack.slice(0, pageIndex + 1).concat(nextCursor);
    setCursorStack(newStack);
    setPageIndex(pageIndex + 1);
  };

  const handlePrev = () => {
    if (pageIndex === 0) return;
    setPageIndex(pageIndex - 1);
  };

  const handleEmailSearch = (e) => {
    e.preventDefault();
    setEmail(emailInput.trim());
  };

  const clearFilters = () => {
    setResource('');
    setEmail('');
    setEmailInput('');
    setFrom('');
    setTo('');
  };

  const hasFilters = resource || email || from || to;
  const displayedEntries = showReads ? entries : entries.filter(e => !isReadEntry(e));

  if (isDemoMode()) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center">
            <FaHistory className="mr-3" /> Activity Log
          </h1>
          <Link to="/" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">Back to Dashboard</Link>
        </div>
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-10 text-center text-gray-500">
          Activity log is not available in demo mode.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <FaHistory className="text-orange-500" /> Activity Log
          </h1>
          <p className="text-sm text-gray-500 mt-1">All successful create, update and delete actions</p>
        </div>
        <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 underline">
          ← Back to Dashboard
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow px-5 py-4 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Module filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Module</label>
            <select
              value={resource}
              onChange={e => setResource(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-jims-cyan"
            >
              {RESOURCE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Date from */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-jims-cyan"
            />
          </div>

          {/* Date to */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-jims-cyan"
            />
          </div>

          {/* User email search */}
          <form onSubmit={handleEmailSearch} className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">User Email</label>
              <input
                type="text"
                placeholder="filter by email..."
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 w-52 focus:outline-none focus:ring-1 focus:ring-jims-cyan"
              />
            </div>
            <button
              type="submit"
              className="flex items-center gap-1 px-3 py-1.5 bg-jims-cyan hover:bg-jims-cyan-dark text-white text-sm rounded"
            >
              <FaSearch className="h-3 w-3" /> Search
            </button>
          </form>

          {/* Show reads toggle */}
          <div className="flex items-center gap-2 ml-auto">
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-600">
              <div
                onClick={() => setShowReads(v => !v)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  showReads ? 'bg-jims-cyan' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                  showReads ? 'translate-x-4' : 'translate-x-1'
                }`} />
              </div>
              Show reads
            </label>
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded"
            >
              <FaTimes className="h-3 w-3" /> Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {error && (
          <div className="px-5 py-4 text-sm text-red-600 bg-red-50 border-b border-red-100">
            <p className="font-semibold mb-1">Error loading logs:</p>
            <pre className="text-xs whitespace-pre-wrap break-all font-mono bg-red-100 rounded p-2">{error}</pre>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <FaSpinner className="animate-spin h-6 w-6 mr-2" /> Loading…
          </div>
        ) : displayedEntries.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            {entries.length > 0
              ? 'No write actions found. Toggle "Show reads" to include read operations.'
              : 'No activity log entries found.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Timestamp', 'User', 'Method', 'Module', 'Resource ID', 'Path', 'Status', ''].map(h => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayedEntries.map(entry => (
                  <LogRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer */}
        {!loading && entries.length > 0 && (
          <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Page {pageIndex + 1}&nbsp;·&nbsp;{displayedEntries.length}{entries.length !== displayedEntries.length ? ` of ${entries.length}` : ''} records
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                disabled={pageIndex === 0}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaChevronLeft className="h-3 w-3" />
              </button>
              <button
                onClick={handleNext}
                disabled={!nextCursor}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogPage;
